/**
 * Text & Markdown Table Export Utility for Protocol Stack
 */

import { 
  getEffectivePrimaryCategory, 
  getEffectiveSecondaryCategory 
} from './healthCategories';

export const AVAILABLE_TABLE_COLUMNS = [
  { id: 'name', label: 'Name', defaultSelected: true, getValue: (p) => p.name },
  { id: 'category', label: 'Compound Type', defaultSelected: true, getValue: (p) => p.category },
  { id: 'primaryCategory', label: 'Primary Goal', defaultSelected: true, getValue: (p) => getEffectivePrimaryCategory(p) },
  { id: 'secondaryCategory', label: 'Secondary Goal', defaultSelected: false, getValue: (p) => getEffectiveSecondaryCategory(p) || 'None' },
  { id: 'cycleStatus', label: 'Status', defaultSelected: true, getValue: (p) => p.cycleStatus },
  { id: 'dosage', label: 'Dosage', defaultSelected: true, getValue: (p) => formatDosage(p) },
  { id: 'weeklyTotalDosage', label: 'Weekly Dosage', defaultSelected: false, getValue: (p) => (p.weeklyTotalDosage !== undefined && p.weeklyTotalDosage !== null && p.weeklyTotalDosage !== '') ? `${p.weeklyTotalDosage} ${p.unit}` : 'N/A' },
  { id: 'administrationFormat', label: 'Format', defaultSelected: true, getValue: (p) => p.administrationFormat || 'N/A' },
  { id: 'timingOfDay', label: 'Timing', defaultSelected: true, getValue: (p) => p.timingOfDay || 'N/A' },
  { id: 'frequencySchedule', label: 'Schedule', defaultSelected: true, getValue: (p) => p.frequencySchedule || 'N/A' },
  { id: 'brandOrSupplier', label: 'Brand/Supplier', defaultSelected: true, getValue: (p) => p.brandOrSupplier || 'N/A' },
  { id: 'intendedOutcome', label: 'Intended Outcome', defaultSelected: true, getValue: (p) => p.intendedOutcome || 'N/A' },
  { id: 'notes', label: 'Notes', defaultSelected: false, getValue: (p) => p.notes || 'N/A' },
  { id: 'startDate', label: 'Start Date', defaultSelected: false, getValue: (p) => p.startDate || 'N/A' },
  { id: 'endDate', label: 'End Date', defaultSelected: false, getValue: (p) => p.endDate || 'N/A' }
];

import { formatCategoryLabel, filterProtocolsByStatusCategory, formatDosageRange } from './sortUtils';

export function filterProtocolsForTable(protocols = [], options = {}) {
  return filterProtocolsByStatusCategory(protocols, options);
}

function formatDosage(protocol) {
  return formatDosageRange(protocol);
}

function cleanCell(value) {
  if (value === null || value === undefined) return 'N/A';
  const str = String(value).trim();
  return str ? str.replace(/\|/g, '\\|').replace(/\r?\n/g, ' ') : 'N/A';
}

function getSelectedColumnDefs(selectedColumnIds) {
  if (!Array.isArray(selectedColumnIds) || selectedColumnIds.length === 0) {
    return AVAILABLE_TABLE_COLUMNS.filter((c) => c.defaultSelected);
  }
  const set = new Set(selectedColumnIds);
  const activeDefs = AVAILABLE_TABLE_COLUMNS.filter((c) => set.has(c.id));
  return activeDefs.length > 0 ? activeDefs : AVAILABLE_TABLE_COLUMNS.filter((c) => c.defaultSelected);
}

/**
 * Generate Markdown Table representation with customizable columns
 */
export function generateMarkdownTable(protocols = [], options = {}) {
  const filtered = filterProtocolsForTable(protocols, options);
  const activeCols = getSelectedColumnDefs(options.selectedColumns);

  if (filtered.length === 0) {
    return '_No protocol records match the selected filter criteria._';
  }

  const headers = activeCols.map((c) => c.label);
  const headerRow = `| ${headers.join(' | ')} |`;
  const separatorRow = `| ${headers.map(() => '---').join(' | ')} |`;

  const dataRows = filtered.map((p) => {
    const cells = activeCols.map((col) => cleanCell(col.getValue(p)));
    return `| ${cells.join(' | ')} |`;
  });

  const categoryLabel = formatCategoryLabel(options.categories, options.category);
  const title = `## Protocol Stack Export (${categoryLabel} - ${options.status || 'Active'})`;
  const timestamp = `_Generated on ${new Date().toLocaleDateString()} via Supps & Peps Stack Tracker_\n`;

  return [title, timestamp, headerRow, separatorRow, ...dataRows].join('\n');
}

/**
 * Generate Plain Text Table representation with clean column padding & customizable columns
 */
export function generatePlainTextTable(protocols = [], options = {}) {
  const filtered = filterProtocolsForTable(protocols, options);
  const activeCols = getSelectedColumnDefs(options.selectedColumns);

  if (filtered.length === 0) {
    return 'No protocol records match the selected filter criteria.';
  }

  // Calculate width for each column based on header & content
  const widths = activeCols.map((col) => {
    let max = col.label.length;
    filtered.forEach((p) => {
      const val = String(col.getValue(p) || 'N/A');
      if (val.length > max) max = val.length;
    });
    return Math.min(max, 36); // Cap width at 36 chars for clean text alignment
  });

  const pad = (str, len) => {
    const s = String(str || 'N/A');
    if (s.length > len) {
      return s.slice(0, len - 3) + '...';
    }
    return s.padEnd(len, ' ');
  };

  const headerLine = activeCols.map((col, i) => pad(col.label.toUpperCase(), widths[i])).join('  ');
  const dividerLine = widths.map((w) => '-'.repeat(w)).join('  ');

  const rows = filtered.map((p) => {
    return activeCols.map((col, i) => pad(col.getValue(p), widths[i])).join('  ');
  });

  const categoryLabel = formatCategoryLabel(options.categories, options.category);
  const banner = `=== PROTOCOL STACK EXPORT (${categoryLabel.toUpperCase()} - ${(options.status || 'ACTIVE').toUpperCase()}) ===`;
  const footer = `\nTotal Compounds: ${filtered.length} | Exported: ${new Date().toLocaleDateString()}`;

  return [banner, '', headerLine, dividerLine, ...rows, footer].join('\n');
}

/**
 * Generate a Discord-friendly, copy-pasteable export.
 *
 * Discord chat messages do not support native markdown tables, and fixed-width
 * ASCII tables break when Discord wraps long lines inside code blocks. Instead,
 * we use a compact, pipe-separated line format with a fixed small set of
 * columns. This fits far more records into Discord's 2000-character limit.
 *
 * If the stack still does not fit, the output is split into clearly-labeled
 * chunks so the user can paste each one as a separate Discord message rather
 * than losing records.
 *
 * The existing Markdown export still covers the “upload a .md file to Discord”
 * use case, where Discord *does* render proper tables in the file preview.
 */
const DISCORD_DEFAULT_MESSAGE_LIMIT = 2000;
const DISCORD_NITRO_MESSAGE_LIMIT = 4000;
const DISCORD_MAX_ESTIMATED_CHUNKS = 999; // conservative label length for greedy sizing

function sanitizeDiscordValue(value) {
  return String(value ?? 'N/A')
    .replace(/\r?\n/g, ' ')
    .replace(/[*_~|`]/g, '')
    .trim();
}

const DISCORD_COMPACT_COLUMNS = [
  { id: 'name', label: 'Name', getValue: (p) => p.name },
  { id: 'cycleStatus', label: 'Status', getValue: (p) => p.cycleStatus },
  { id: 'dosage', label: 'Dosage', getValue: (p) => formatDosage(p) },
  { id: 'frequencySchedule', label: 'Schedule', getValue: (p) => p.frequencySchedule || 'N/A' },
  { id: 'category', label: 'Type', getValue: (p) => p.category },
  { id: 'primaryCategory', label: 'Goal', getValue: (p) => getEffectivePrimaryCategory(p) }
];

export function generateDiscordTable(protocols = [], options = {}) {
  const filtered = filterProtocolsForTable(protocols, options);

  if (filtered.length === 0) {
    return '_No protocol records match the selected filter criteria._';
  }

  const messageLimit =
    typeof options.messageLimit === 'number' && options.messageLimit > 0
      ? options.messageLimit
      : DISCORD_DEFAULT_MESSAGE_LIMIT;

  const activeCols = DISCORD_COMPACT_COLUMNS;
  const headerLine = activeCols.map((c) => c.label).join(' | ');

  const rows = filtered.map((p, index) => {
    const values = activeCols.map((c) => sanitizeDiscordValue(c.getValue(p))).join(' | ');
    return `**${index + 1}.** ${values}`;
  });

  const categoryLabel = formatCategoryLabel(options.categories, options.category);
  const title = `**Protocol Stack Export (${categoryLabel} – ${options.status || 'Active'})**`;
  const footer = `_Exported ${new Date().toLocaleDateString()} via Supps & Peps_`;

  const buildChunk = (start, end, chunkNum, totalChunks) => {
    const chunkTitle = totalChunks > 1
      ? `**[Message ${chunkNum}/${totalChunks}]** ${title}`
      : title;
    const body = rows.slice(start, end).join('\n');
    return `${chunkTitle}\n\n${headerLine}\n${body}\n\n${footer}`;
  };

  // Try to fit everything into a single Discord message first.
  const single = buildChunk(0, rows.length, 1, 1);
  if (single.length <= messageLimit) {
    return single;
  }

  // Split into chunks so no records are silently dropped.
  // Use a conservative 3-digit total chunk count when sizing so the final
  // labels (which may be shorter) are guaranteed to fit.
  const chunks = [];
  let start = 0;
  while (start < rows.length) {
    let end = start;
    while (
      end < rows.length &&
      buildChunk(start, end + 1, chunks.length + 1, DISCORD_MAX_ESTIMATED_CHUNKS).length <= messageLimit
    ) {
      end++;
    }
    // If even one row is too long, include it anyway so we never hang.
    if (end === start) end = start + 1;
    chunks.push({ start, end });
    start = end;
  }

  const total = chunks.length;
  return chunks
    .map((c, i) => buildChunk(c.start, c.end, i + 1, total))
    .join('\n\n--- paste the above as a separate Discord message ---\n\n');
}

export { DISCORD_DEFAULT_MESSAGE_LIMIT, DISCORD_NITRO_MESSAGE_LIMIT };

